import React, { useState, forwardRef, useEffect, useCallback } from 'react'
import { Stack, Box, Text, TextInput, Button, Card, Flex, Dialog } from '@sanity/ui'
import { set } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

interface SingleProductProps extends ObjectInputProps {
  // Add any additional props if needed
}

interface Product {
  id: number
  name: string
  sku: string
}

interface ProductValue {
  id: number | null
  name: string | null
  sku: string | null
}

const SingleProduct = forwardRef<HTMLInputElement, SingleProductProps>((props, ref) => {
  const { document, value, onChange } = props
  const client = useClient()
  
  // State management
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Get frontend URL from environment or fallback
  const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

  // Handlers
  const updateQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }, [])

  const onFormSubmit = useCallback(async () => {
    setSearchError('')

    try {
      setSearching(true)

      const params = new URLSearchParams({
        sku: query,
        type_id: 'simple',
      })

      const response = await fetch(`${baseURL}/api/callbacks/search-products?${params}`)
      const data = await response.json()

      if (data?.error) {
        setSearchError(data.error)
        return
      }

      if (data?.length) {
        const results = data.reduce((map: Product[], product: any) => {
          // keep only relevant information
          map.push({
            sku: product.sku,
            name: product.name,
            id: product.id,
          })
          return map
        }, [])

        if (!results.length) {
          setSearchError('There are no new products matching your search criteria.')
          return
        }

        setSearchResults(results)
        setDialogOpen(true)
      } else {
        setSearchError('No products found matching your search criteria.')
      }
    } catch (e) {
      console.log('Search prods err: ', (e as Error).message)
      setSearchError('An error occurred while searching for products.')
    } finally {
      setSearching(false)
    }
  }, [baseURL, query])

  const onDialogClose = useCallback(() => {
    setSearchResults([])
    setDialogOpen(false)
  }, [])

  const selectProduct = useCallback((product?: Product) => {
    if (!isLoaded) {
      return
    }

    if (product) {
      const { sku, id, name } = product
      const productPartial = { sku, id, name }
      onChange(set(productPartial))
    }

    setSearchResults([])
    setDialogOpen(false)
  }, [isLoaded, onChange])

  const removeProduct = useCallback(() => {
    onChange(
      set({
        id: null,
        sku: null,
        name: null,
      })
    )
  }, [onChange])

  // Initialize component
  useEffect(() => {
    setIsLoaded(true)
  }, [])

  // Load existing value when document is loaded
  useEffect(() => {
    if (!document?._createdAt) return
    // delay setting local data slightly
    // otherwise we might get empty data
    setTimeout(() => {
      if (value) {
        selectProduct(value as Product)
      }
    }, 100)
  }, [document?._createdAt])

  const productValue = value as ProductValue

  return (
    <Stack gap={4}>
      {/* Search Interface - shown when no product is selected */}
      {!productValue?.sku && (
        <Card padding={4} tone="transparent" border>
          <Stack gap={3}>
            <TextInput
              placeholder="Search by SKUs (comma separated)"
              value={query}
              onChange={updateQuery}
              ref={ref}
            />
            
            <Flex justify="center">
              <Button
                text={searching ? 'Searching...' : 'Search products'}
                onClick={onFormSubmit}
                disabled={searching}
                tone="primary"
              />
            </Flex>

            {searchError && (
              <Card padding={3} tone="critical">
                <Text size={1}>{searchError}</Text>
              </Card>
            )}
          </Stack>
        </Card>
      )}

      {/* Search Results Dialog */}
      {dialogOpen && searchResults?.length > 0 && (
        <Dialog
          header={`${searchResults?.length} products found`}
          id="search-results"
          onClose={onDialogClose}
          width={1}
          zOffset={1000}
        >
          <Box padding={4}>
            <Stack gap={3}>
              {searchResults.map((product, i) => (
                <Card key={`sku${i}`} padding={3} tone="transparent" border>
                  <Flex justify="space-between" align="flex-start">
                    <Stack gap={3}>
                      <Text size={1} weight="medium">{product.name}</Text>
                      <Text size={1} muted>{product.sku}</Text>
                    </Stack>
                    <Button
                      text="Select"
                      onClick={() => selectProduct(product)}
                      mode="default"
                      fontSize={1}
                    />
                  </Flex>
                </Card>
              ))}
            </Stack>
          </Box>
        </Dialog>
      )}

      {/* Selected Product Display */}
      {productValue?.sku && (
        <Card padding={4} tone="primary" border>
          <Flex justify="space-between" align="flex-start">
            <Stack gap={3}>
              <Text size={1} weight="medium">{productValue.name}</Text>
              <Text size={1} muted>{productValue.sku}</Text>
            </Stack>
            <Button
              text="Remove"
              onClick={removeProduct}
              mode="ghost"
              tone="critical"
              fontSize={1}
            />
          </Flex>
        </Card>
      )}
    </Stack>
  )
})

SingleProduct.displayName = 'SingleProduct'

export default SingleProduct